import { Switch } from 'radix-ui';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';

interface RandomToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  id?: string;
  /** Override label text colour for world-tinted Builder routes (e.g.
   *  `text-dream-white` on KP's navy bg). Defaults to `text-dream-navy`. */
  labelClassName?: string;
  /** Override Switch.Root track classes for world-tinted Builder routes
   *  (e.g. white-translucent pill on KP's navy bg, where the default
   *  `bg-dream-navy/25 data-[state=checked]:bg-dream-navy` track is
   *  invisible). */
  trackClassName?: string;
}

export function RandomToggle({
  checked,
  onCheckedChange,
  id,
  labelClassName,
  trackClassName,
}: RandomToggleProps) {
  const labelId = id ?? 'random-toggle';
  return (
    <div className="flex items-center gap-3">
      <Switch.Root
        id={labelId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors',
          'bg-dream-navy/25 data-[state=checked]:bg-dream-navy',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dream-navy/60 focus-visible:ring-offset-2',
          trackClassName,
        )}
      >
        <Switch.Thumb
          className={cn(
            'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-md transition-transform',
            'translate-x-0.5 data-[state=checked]:translate-x-[22px]',
          )}
        />
      </Switch.Root>
      <label
        htmlFor={labelId}
        className={cn(
          'cursor-pointer text-sm text-dream-navy',
          checked && 'font-bold',
          labelClassName,
        )}
      >
        {checked
          ? STRINGS.builder.shared.randomToggleLabelActive
          : STRINGS.builder.shared.randomToggleLabel}
      </label>
    </div>
  );
}
